/*
 * OCC.4 (28 Sep 2026). Where one KSA row lands, for EVERY surface that counts
 * a learner's skills against an occupation.
 *
 * SHARED ON PURPOSE, like goals-report-record.ts. Three places used to decide
 * this for themselves:
 *
 * - the occupation page's Skills tab (`readiness-ksa-match.ts` in apps/cpd),
 * - the Goals Report skills card (`getSkillsCompatibilityFromEngine` in the
 *   subgraph, which ran its own SQL), and
 * - the Via report payload (`selectReportSkills`).
 *
 * The second one drifted. It counted every profile row, including the
 * aptitude and baseline rows the Skills tab never shows, credited only HELD
 * matches (never a row a completed program names), and filed every row nothing
 * measured under "not yet". Measured on dev for one learner and one goal: the
 * Skills tab said 1 of 77 met, the Goals Report said 0 of 135 on the record,
 * 135 not yet. Same learner, same occupation, two different stories.
 *
 * So the rule lives here once and both apps import it. Pure: no Angular, no
 * Nest, no database.
 */

/** The server's mark on one KSA row (`OccupationKsa.matchStatus`). */
export type SharedKsaMatchStatus =
  | 'MET'
  | 'NAMED'
  | 'GAP'
  | 'UNMEASURED'
  | 'BASELINE';

/**
 * Where a row goes.
 *
 * `met` carries MET and NAMED (Jackson, 22 Sep: a completed program naming a
 * skill sits with the things the learner has). `gap` is a measured shortfall,
 * and an unmeasured row for a learner whose record we could read (Jackson's
 * ruling of 22 Sep). `unmatched` is the neutral "not checked" list: rows
 * nothing compared. `hidden` is the OCC.4 baseline floor, shown and counted
 * nowhere.
 */
export type SharedKsaColumn = 'met' | 'gap' | 'unmatched' | 'hidden';

function norm(status: string | null | undefined): string {
  return (status ?? '').trim().toUpperCase();
}

/**
 * Did anything the learner HOLDS rate a single element of this profile?
 *
 * MET or GAP is the proof: both need a held level to compare against. NAMED,
 * UNMEASURED and BASELINE can all arise with nothing held at all. Binary on
 * purpose (constitution 10.3, no invented thresholds); see OCC.4 section 4.
 */
export function ksaRecordIsReadable(
  statuses: Iterable<string | null | undefined>
): boolean {
  for (const status of statuses) {
    const s = norm(status);
    if (s === 'MET' || s === 'GAP') {
      return true;
    }
  }
  return false;
}

/** The column one row belongs in. An unknown or absent mark is `unmatched`. */
export function ksaColumnFor(
  status: string | null | undefined,
  recordReadable = true
): SharedKsaColumn {
  switch (norm(status)) {
    case 'MET':
    case 'NAMED':
      return 'met';
    case 'GAP':
      return 'gap';
    case 'UNMEASURED':
      // For a learner we could read, an unmeasured row is something the
      // occupation asks for that the record does not show: a gap. For a
      // learner whose record rated nothing it is every row at once, and a
      // page of "not yet" against skills nobody checked is not a finding.
      return recordReadable ? 'gap' : 'unmatched';
    case 'BASELINE':
      return 'hidden';
    default:
      return 'unmatched';
  }
}

/**
 * The four families, as an allowlist, exactly as `familyLabelFor` in the
 * Skills tab. `software` is a real element_type and is the banned content.
 */
const KSA_FAMILIES = new Set([
  'knowledge',
  'skill',
  'skills',
  'ability',
  'abilities',
  'work_activity',
  'work_activities',
]);

export function isKsaFamily(type: string | null | undefined): boolean {
  return KSA_FAMILIES.has((type ?? '').trim().toLowerCase());
}

/** One `OccupationKsa` row, as far as this file needs it. */
export interface SharedKsaRow {
  id?: string | null;
  name?: string | null;
  type?: string | null;
  matchStatus?: string | null;
}

/**
 * The Goals Report's `SkillCompatibility`, built by the Skills tab's rules.
 *
 * `matchesUser` has THREE values and the third is the point of this fix:
 * `true` is met, `false` is a gap, `null` is NOT COMPARED. The GraphQL field
 * was always a nullable Boolean, so no schema change is needed. Hidden rows
 * leave the list, exactly as they leave the Skills tab and its total.
 *
 * Returns an empty list when NO row carries a mark at all: the flag is off,
 * the skills schema is absent, or the match did not run. That empty list is
 * the caller's signal to fall back to the older comparison, unchanged.
 */
export function ksaRowsToSkillsCompatibility(
  rows: SharedKsaRow[] | null | undefined
): { text: string; matchesUser: boolean | null }[] {
  const usable = (rows ?? []).filter(
    (row) => !!row?.id && !!(row?.name ?? '').trim() && isKsaFamily(row?.type)
  );

  if (!usable.some((row) => !!norm(row.matchStatus))) {
    return [];
  }

  const readable = ksaRecordIsReadable(usable.map((row) => row.matchStatus));

  return usable
    .map((row) => ({ row, column: ksaColumnFor(row.matchStatus, readable) }))
    .filter(({ column }) => column !== 'hidden')
    .map(({ row, column }) => ({
      text: (row.name ?? '').trim(),
      matchesUser: column === 'met' ? true : column === 'gap' ? false : null,
    }));
}

/** Met / gap / not compared, and the visible total they add up to. */
export interface KsaTally {
  met: number;
  gap: number;
  notCompared: number;
  total: number;
}

/**
 * Counts a `SkillCompatibility` list the way the Skills tab counts columns.
 *
 * `null` is not compared. `undefined` is read as a gap, because that is what
 * the older detailed-work-activity rows (and every fixture written before
 * this) meant by a missing flag.
 */
export function tallySkillsCompatibility(
  items: { matchesUser?: boolean | null }[] | null | undefined
): KsaTally {
  const list = items ?? [];
  const met = list.filter((item) => item?.matchesUser === true).length;
  const notCompared = list.filter((item) => item?.matchesUser === null).length;
  return {
    met,
    gap: list.length - met - notCompared,
    notCompared,
    total: list.length,
  };
}
